import pickBy from 'lodash/pickBy'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  coatingThicknessFormField,
  installationDateFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import {
  GeckoRectangularManway,
  GeckoRectangularManwayJSON,
} from 'types/assetModel/features/geckoRectangularManway'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
} from 'types/assetModel/features/welds/geckoHorizontalWeld'
import {
  GeckoPipeCircumferentialWeld,
  GeckoPipeCircumferentialWeldJSON,
} from 'types/assetModel/features/welds/geckoPipeCircumferentialWeld'
import {
  GeckoPipeSeamWeld,
  GeckoPipeSeamWeldJSON,
} from 'types/assetModel/features/welds/geckoPipeSeamWeld'
import {
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types/assetModel/features/welds/geckoVerticalWeld'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { forecastMath } from 'utils/fcMath'
import { getFormattedInstallationDateString } from 'utils/installationDateHelpers'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

export const PressureVesselShellAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.PipeCircumferentialWeld,
  GeckoFeatureModelType.PipeSeamWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.SkirtSupport,
  GeckoFeatureModelType.SaddleSupport,
  GeckoFeatureModelType.LegSupport,
  GeckoFeatureModelType.RectangularManway,
]
export enum PressureVesselShellOrientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export type PressureVesselShellAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PressureVesselShell
  length: number
  radius: number
  // TODO: figure out which features are needed for pressure vessel shell
  features: Record<
    string,
    | GeckoRectangularManwayJSON
    | GeckoPipeCircumferentialWeldJSON
    | GeckoPipeSeamWeldJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
  >
  orientation: PressureVesselShellOrientation
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness: number | undefined
  }
  compass_orientation: number | undefined
  material: string | undefined
  installation_date: string | undefined
  lining_type: string | undefined
  coating_type: string | undefined
  coating_thickness: number | undefined
  insulation_type: string | undefined
  insulation_thickness: number | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined
  tmin: number | undefined
  design_pressure: number | undefined
}

export const NewPressureVesselShellAssetModelJSON = {
  name: 'New Pressure Vessel Shell',
  type: GeckoComponentModelType.PressureVesselShell,
  length: 0,
  radius: 0,
  orientation: PressureVesselShellOrientation.Vertical,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  nominal: {
    wall_thickness: undefined,
  },
  compass_orientation: undefined,
  material: undefined,
  installation_date: undefined,
  lining_type: undefined,
  coating_type: undefined,
  coating_thickness: undefined,
  insulation_type: undefined,
  insulation_thickness: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,
  tmin: undefined,
  design_pressure: undefined,
} as PressureVesselShellAssetModelJSON

export class GeckoPressureVesselShell extends GeckoEditableComponent {
  type: GeckoComponentModelType.PressureVesselShell
  radius: GeckoMeasurement
  length: GeckoMeasurement
  orientation: PressureVesselShellOrientation
  material: string | undefined
  installationDate: Date | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  rectangularManways: Map<string, GeckoRectangularManway>
  circumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>
  seamWelds: Map<string, GeckoPipeSeamWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  plates: Map<string, GeckoPlate>
  wallNominalThickness: GeckoMeasurement | undefined
  compassOrientation: number | undefined
  liningType: string | undefined
  coatingType: string | undefined
  coatingThickness: GeckoMeasurement | undefined
  insulationType: string | undefined
  insulationThickness: GeckoMeasurement | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  designPressure: GeckoMeasurement | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    length: GeckoMeasurement,
    orientation: PressureVesselShellOrientation,
    material: string | undefined,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    rectangularManways: Map<string, GeckoRectangularManway>,
    circumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>,
    seamWelds: Map<string, GeckoPipeSeamWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    plates: Map<string, GeckoPlate>,
    wallNominalThickness: GeckoMeasurement | undefined,
    compassOrientation: number | undefined,
    liningType: string | undefined,
    coatingType: string | undefined,
    coatingThickness: GeckoMeasurement | undefined,
    insulationType: string | undefined,
    insulationThickness: GeckoMeasurement | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    designPressure: GeckoMeasurement | undefined
  ) {
    super(id, name, json)

    this.type = GeckoComponentModelType.PressureVesselShell
    this.radius = radius
    this.length = length
    this.orientation = orientation
    this.material = material
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.nozzles = nozzles
    this.manways = manways
    this.rectangularManways = rectangularManways
    this.circumferentialWelds = circumferentialWelds
    this.seamWelds = seamWelds
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.plates = plates
    this.wallNominalThickness = wallNominalThickness
    this.compassOrientation = compassOrientation
    this.liningType = liningType
    this.coatingType = coatingType
    this.coatingThickness = coatingThickness
    this.insulationType = insulationType
    this.insulationThickness = insulationThickness
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.tmin = tmin
    this.designPressure = designPressure
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public get rectangularManwaysArray() {
    return Array.from(this.rectangularManways.values())
  }

  public get circumferentialWeldsArray() {
    return Array.from(this.circumferentialWelds.values())
  }
  public get seamWeldsArray() {
    return Array.from(this.seamWelds.values())
  }
  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }
  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }
  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public static CreateFromJSON(
    id: string,
    json: PressureVesselShellAssetModelJSON
  ) {
    const name = json.name
    const shellRadius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const shellLength = GeckoMeasurement.CreateFromValue(
      json.length,
      GeckoMeasurementUnit.Meters
    )

    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      json.component_bounds.x_start,
      json.component_bounds.y_start,
      json.component_bounds.x_end,
      json.component_bounds.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config

    const featureData = json.features

    const orientation = json.orientation as PressureVesselShellOrientation

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    }) as Record<string, GeckoNozzleJSON>
    const nozzles = GeckoNozzle.CreateMapFromJSON(id, childNozzleJSON)

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    }) as Record<string, GeckoManwayJSON>
    const manways = GeckoManway.CreateMapFromJSON(id, childManwayJSON)

    const childVerticalWeldsJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    }) as Record<string, GeckoVerticalWeldJSON>
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldsJSON
    )

    const childHorizontalWeldsJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    }) as Record<string, GeckoHorizontalWeldJSON>
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldsJSON
    )

    const childSeamWeldsJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeSeamWeld
    }) as Record<string, GeckoPipeSeamWeldJSON>
    const seamWelds = GeckoPipeSeamWeld.CreateMapFromJSON(
      id,
      childSeamWeldsJSON
    )

    const childRectangularManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.RectangularManway
    }) as Record<string, GeckoRectangularManwayJSON>
    const rectangularManways = GeckoRectangularManway.CreateMapFromJSON(
      id,
      childRectangularManwayJSON
    )

    const childCircumferentialWeldsJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeCircumferentialWeld
    }) as Record<string, GeckoPipeCircumferentialWeldJSON>

    const circumferentialWelds = GeckoPipeCircumferentialWeld.CreateMapFromJSON(
      id,
      childCircumferentialWeldsJSON,
      componentBounds
    )

    const childPlatesJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    }) as Record<string, GeckoPlateJSON>

    const plates = GeckoPlate.CreateMapFromJSON(id, childPlatesJSON)
    const parsedWallNominalThickness = parseNumber(json.nominal.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const compassOrientation = parseNumber(json.compass_orientation)

    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)
    const liningType = parseString(json.lining_type)
    const coatingType = parseString(json.coating_type)
    const parsedCoatingThickness = parseNumber(json.coating_thickness)
    const coatingThickness =
      parsedCoatingThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedCoatingThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined
    const insulationType = parseString(json.insulation_type)
    const parsedInsulationThickness = parseNumber(json.insulation_thickness)
    const insulationThickness =
      parsedInsulationThickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedInsulationThickness,
            GeckoMeasurementUnit.Millimeters
          )
        : undefined
    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const designPressureJson = json.design_pressure
    const parsedDesignPressure = parseNumber(designPressureJson)
    const designPressure =
      parsedDesignPressure !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedDesignPressure,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    return new GeckoPressureVesselShell(
      id,
      json,
      name,
      shellRadius,
      shellLength,
      orientation,
      material,
      installationDate,
      componentBounds,
      sceneConfig,
      nozzles,
      manways,
      rectangularManways,
      circumferentialWelds,
      seamWelds,
      verticalWelds,
      horizontalWelds,
      plates,
      wallNominalThickness,
      compassOrientation,
      liningType,
      coatingType,
      coatingThickness,
      insulationType,
      insulationThickness,
      jointEfficiency,
      stressTolerance,
      tmin,
      designPressure
    )
  }

  public asJSON(): PressureVesselShellAssetModelJSON {
    return generatePressureVesselShell(
      this.name,
      this.length,
      this.radius,
      this.orientation,
      this.jointEfficiency,
      this.stressTolerance,
      this.tmin,
      this.designPressure,
      this.wallNominalThickness,
      this.installationDate,
      this.compassOrientation,
      this.json as PressureVesselShellAssetModelJSON
    )
  }
}

const generatePressureVesselShell = (
  name: string,
  length: GeckoMeasurement,
  radius: GeckoMeasurement,
  orientation: PressureVesselShellOrientation,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  designPressure: GeckoMeasurement | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  installationDate: Date | undefined,
  compassOrientation: number | undefined,
  otherShellData: PressureVesselShellAssetModelJSON = NewPressureVesselShellAssetModelJSON
): PressureVesselShellAssetModelJSON => {
  const shellLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const shellRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const shellCircumference = Number(
    (2 * pi * shellRadius).toPrecision(assetModelPrecision)
  )

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedDesignPressure =
    designPressure !== undefined
      ? designPressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PressureVesselShell,
    length: shellLength,
    radius: shellRadius,
    orientation: orientation,
    features: otherShellData.features,
    scene_config: otherShellData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: shellCircumference,
      y_end: shellLength,
    },
    nominal: {
      wall_thickness: wallNominalThicknessMeters,
    },
    compass_orientation: compassOrientation,
    material: otherShellData.material,
    installation_date: installationDate
      ? getFormattedInstallationDateString(installationDate)
      : undefined,
    lining_type: otherShellData.lining_type,
    coating_type: otherShellData.coating_type,
    coating_thickness: otherShellData.coating_thickness,
    insulation_type: otherShellData.insulation_type,
    insulation_thickness: otherShellData.insulation_thickness,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    tmin: tminMeters,
    design_pressure: parsedDesignPressure,
  }
}

export const PressureVesselShellAssetModelFormFields = (
  shellJSON: PressureVesselShellAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Shell Name',
      value: shellJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'length',
      label: 'Length / Height',
      value: shellJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: shellJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'orientation',
      label: 'Shell Orientation',
      value: shellJSON.orientation,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: getDropdownOptionsForEnum(PressureVesselShellOrientation),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    wallThicknessFormField(shellJSON.nominal.wall_thickness),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: shellJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: shellJSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tmin',
      label: 'Tmin',
      value: shellJSON.tmin,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'design_pressure',
      label: 'Design Pressure',
      value: shellJSON.design_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material Type',
      value: shellJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(shellJSON.installation_date, true),
    {
      dataKey: 'lining_type',
      label: 'Lining Type',
      value: shellJSON.lining_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coating_type',
      label: 'Coating Type',
      value: shellJSON.coating_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    coatingThicknessFormField(shellJSON.coating_thickness),
    {
      dataKey: 'insulation_type',
      label: 'Insulation Type',
      value: shellJSON.insulation_type,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'insulation_thickness',
      label: 'Insulation Thickness',
      value: shellJSON.insulation_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PressureVesselShellJSONFromFormFields = (
  component: PressureVesselShellAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const parsedDesignPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'design_pressure')?.value
  )

  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )

  const length = getFormFieldByDataKey(formFields, 'length')?.value as number
  const radius =
    (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2

  const shellCircumference = 2 * Math.PI * radius

  const updatedComponent: PressureVesselShellAssetModelJSON = {
    ...component,
    name: getFormFieldByDataKey(formFields, 'name')?.value as string,
    length: length,
    radius: radius,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: shellCircumference,
      y_end: length,
    },
    orientation: getFormFieldByDataKey(formFields, 'orientation')
      ?.value as PressureVesselShellOrientation,
    nominal: {
      wall_thickness: parsedWallNominalThickness,
    },
    compass_orientation: compassOrientation,
    material: parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installation_date: parseString(
      getFormFieldByDataKey(formFields, 'installation_date')?.value
    ),
    lining_type: parseString(
      getFormFieldByDataKey(formFields, 'lining_type')?.value
    ),
    coating_type: parseString(
      getFormFieldByDataKey(formFields, 'coating_type')?.value
    ),
    coating_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'coating_thickness')?.value
    ),
    insulation_type: parseString(
      getFormFieldByDataKey(formFields, 'insulation_type')?.value
    ),
    insulation_thickness: parseNumber(
      getFormFieldByDataKey(formFields, 'insulation_thickness')?.value
    ),
    joint_efficiency: parsedJointEfficiency,
    stress_tolerance: parsedStressTolerance,
    tmin: parsedTmin,
    design_pressure: parsedDesignPressure,
  }

  return updatedComponent
}
